/**
 * Homepage demo only: turns one supplier-style line into the same fields the
 * real supplier-menu card (`MenuListingCard`) renders. Pure and client-side —
 * nothing is saved, sent, or looked up.
 *
 * Order- and punctuation-tolerant: each field is pulled out of the whole line
 * by pattern, and whatever is left (minus filler words) is the strain name.
 * Deliberately not AI — this runs for anonymous visitors on every keystroke.
 */
export interface DemoListing {
  id: string;
  strain_name: string;
  product_form: string | null;
  dimension: 'mass' | 'count';
  quantity_value: number | null;
  quantity_unit: string | null;
  price_value: number | null;
  price_unit: string | null;
  thc_percent: number | null;
  currency: string;
  /** Illustrative passed tests shown when the line mentions a COA. */
  compliance: Record<string, boolean> | null;
}

/** Longest phrases first so "small buds" wins over "buds". */
const FORMS: [RegExp, string][] = [
  [/\bsmall\s*buds?\b|\bsmalls\b/, 'smalls'],
  [/\blive\s*resin\b/, 'live resin'],
  [/\blive\s*rosin\b/, 'live rosin'],
  [/\bpre[\s-]?rolls?\b/, 'pre-rolls'],
  [/\ba\s*\/\s*b\b/, 'a/b flower'],
  [/\ba[\s-]?grade\b|\ba[\s-]?flower\b/, 'a-grade'],
  [/\bb[\s-]?grade\b|\bb[\s-]?flower\b/, 'b-grade'],
  [/\bbigs\b/, 'bigs'],
  [/\btrim\b/, 'trim'],
  [/\bshake\b/, 'shake'],
  [/\bdistillate\b/, 'distillate'],
  [/\brosin\b/, 'rosin'],
  [/\bkief\b/, 'kief'],
  [/\bhash\b/, 'hash'],
  [/\bconcentrates?\b/, 'concentrate'],
  [/\bflower\b/, 'flower'],
];

const UNIT: Record<string, string> = {
  lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb', p: 'lb', kg: 'kg', kgs: 'kg', kilo: 'kg', kilos: 'kg',
  g: 'g', gram: 'g', grams: 'g', unit: 'unit', units: 'unit', ea: 'unit', each: 'unit', pcs: 'unit',
};
const UNIT_RE = '(lbs?|pounds?|kgs?|kilos?|grams?|g|units?|ea|each|pcs)';

const FILLER = /\b(coa|attached|available|avail|tested|lab|labs|with|w\/|and|of|per|at|ready|now)\b/gi;

const DEMO_COMPLIANCE = {
  no_pesticides: true,
  no_heavy_metals: true,
  microbials_pass: true,
  no_mycotoxins: true,
};

const num = (s: string) => Number(s.replace(/,/g, ''));
const titleCase = (s: string) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export function parseDemoLine(line: string): DemoListing | null {
  let rest = ` ${line.toLowerCase()} `;
  const take = (re: RegExp) => {
    const m = rest.match(re);
    if (m) rest = rest.replace(m[0], ' ');
    return m;
  };

  const hasCoa = /\bcoa\b/.test(rest);

  // THC: "THC 24%", "thc:24", "24% THC", "24 % thc"
  const thcM = take(/\bthc\s*:?\s*([\d.]+)\s*%?/) ?? take(/([\d.]+)\s*%\s*thc\b/) ?? take(/([\d.]+)\s*%/);
  // Price: "$250/lb", "$250 per lb", "$ 3.50 / g", "250/lb"
  const priceM =
    take(new RegExp(`\\$\\s?([\\d,.]+)\\s*(?:\\/|per|a)?\\s*${UNIT_RE}?\\b`)) ??
    take(new RegExp(`\\b([\\d,.]+)\\s*(?:\\/|per)\\s*${UNIT_RE}\\b`));
  // Quantity: "40lbs", "40 lb", "1,000 units"
  const qtyM = take(new RegExp(`\\b([\\d,.]+)\\s*${UNIT_RE}\\b`));

  let form: string | null = null;
  for (const [re, label] of FORMS) {
    if (take(re)) { form = label; break; }
  }

  const strain = rest
    .replace(FILLER, ' ')
    .replace(/[^a-z0-9#'\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!strain || !/[a-z]/.test(strain)) return null;

  const qty = qtyM ? { v: num(qtyM[1]), u: UNIT[qtyM[2]] } : null;
  const price = priceM ? { v: num(priceM[1]), u: priceM[2] ? UNIT[priceM[2]] : null } : null;
  const isCount = qty?.u === 'unit' || price?.u === 'unit' || form === 'pre-rolls';

  return {
    id: 'demo-listing',
    strain_name: titleCase(strain),
    product_form: form,
    dimension: isCount ? 'count' : 'mass',
    quantity_value: qty?.v ?? null,
    quantity_unit: qty?.u ?? null,
    price_value: price?.v ?? null,
    price_unit: price?.u ?? qty?.u ?? null,
    thc_percent: thcM ? num(thcM[1]) : null,
    currency: 'USD',
    compliance: hasCoa ? DEMO_COMPLIANCE : null,
  };
}
